/**
 * The plain mends the setup builder makes to a drafted record before the
 * author reads it, shared so the studio's reducer makes the same ones
 * silently on every batch (J2.9): a worked-out field or a field of lines is
 * never required, the headline sits on a field that can name a row, and the
 * form opens on a field it can open on.
 */
import type { SetupDraftField } from '../compose-author';
import { PURPOSES } from '../generated/purposes';

type DraftFields = SetupDraftField[];

/** The field types that can name a row: what the author's title purpose is answered by. */
const NAMES_A_ROW: ReadonlySet<string> = new Set(PURPOSES.title.answered_by);

/** The field types the form can open on: what the author's focused_input purpose is answered by. */
const TAKES_FOCUS: ReadonlySet<string> = new Set(PURPOSES.focused_input.answered_by);

/** The types nobody fills in, which the author refuses as required: a worked-out field and a field of lines. */
const NEVER_REQUIRED: ReadonlySet<string> = new Set(['formula', 'lines']);

/**
 * A worked-out field or a field of lines never `required`, at every level.
 * The author refuses either (FORMULA_NEVER_REQUIRED: nobody fills it;
 * LINES_NEVER_REQUIRED: an entry may hold no lines), and a bill that must be
 * there or a stay that must have rooms is what a model marks so first. The
 * mend is plain, so it is made before the author reads the draft and costs no
 * repair call (5 Oct 2026: a hotel's fields stage spent its one repair on its
 * required rooms and came back with another fault; a salon's spent it on a
 * required Bill, and a Bill still refused is one the salvage drops, which
 * took the sum of the services with it).
 */
export const unrequireUnfilled = (fields: DraftFields): DraftFields => {
  let changed = false;
  const out = fields.map((field) => {
    const inner = field.lines === undefined ? undefined : unrequireUnfilled(field.lines.fields);
    const moved = inner !== undefined && inner !== field.lines?.fields;
    if (field.required === undefined || !NEVER_REQUIRED.has(field.type)) {
      if (!moved) return field;
      changed = true;
      return { ...field, lines: { ...field.lines!, fields: inner } };
    }
    changed = true;
    const { required: _never, ...rest } = field;
    return moved ? { ...rest, lines: { ...field.lines!, fields: inner } } : rest;
  });
  return changed ? out : fields;
};

/**
 * The headline, on a field that can name a row. A record whose every named
 * field was a date, a figure or its lines (a hotel's stay: check-in,
 * check-out, rooms) came back headed by its check-in date, which the author
 * refuses (PURPOSE_TYPE_MISMATCH); it moves to the first field that can name
 * a row. With none, it stays, and the author says so.
 */
export const headlineFieldOf = (fields: DraftFields, headline: string): string => {
  const held = fields.find((field) => field.key === headline);
  if (held === undefined || NAMES_A_ROW.has(held.type)) return headline;
  return fields.find((field) => NAMES_A_ROW.has(field.type))?.key ?? headline;
};

/** Whether any of these fields can name a row (a text, a phone, a choice, a pick...). */
export const namesARow = (fields: ReadonlyArray<Pick<SetupDraftField, 'type'>>): boolean => fields.some((field) => NAMES_A_ROW.has(field.type));

/**
 * A headless record's headline (a built record no field could name a row of,
 * `StudioRecord.headless`): the one it has when that field can name a row,
 * else the first field that can, else none ('').
 */
export const headlessHeadlineOf = (fields: DraftFields, headline: string): string => {
  const held = fields.find((field) => field.key === headline);
  if (held !== undefined && NAMES_A_ROW.has(held.type)) return headline;
  return fields.find((field) => NAMES_A_ROW.has(field.type))?.key ?? '';
};

/**
 * The field the form opens on, or none when it names one the form cannot
 * open on (a salon visit's ticked services): the author refuses that
 * (PURPOSE_TYPE_MISMATCH), and with none the form opens on its first field.
 */
export const focusFieldOf = (fields: DraftFields, focus: string | undefined): string | undefined => {
  const held = focus === undefined ? undefined : fields.find((field) => field.key === focus);
  return held === undefined || TAKES_FOCUS.has(held.type) ? focus : undefined;
};

/** A record with the plain mends made: the same object when none was needed. */
export const mendDraftRecord = <R extends { fields: DraftFields; headlineFieldKey: string; focusFieldKey?: string; headless?: true }>(record: R): R => {
  const fields = unrequireUnfilled(record.fields);
  const headlineFieldKey = record.headless === true ? headlessHeadlineOf(fields, record.headlineFieldKey) : headlineFieldOf(fields, record.headlineFieldKey);
  const focusFieldKey = focusFieldOf(fields, record.focusFieldKey);
  if (fields === record.fields && headlineFieldKey === record.headlineFieldKey && focusFieldKey === record.focusFieldKey) return record;
  const { focusFieldKey: _focus, ...rest } = record;
  return { ...rest, fields, headlineFieldKey, ...(focusFieldKey === undefined ? {} : { focusFieldKey }) } as R;
};
